import { DatePipe, NgClass } from '@angular/common';
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';

import { Logger } from '../../../core/logging/logger.service';
import {
  NumeronCollectionModel,
  NumeronItemModel,
} from '../../../features/numerons/models/numeron.model';
import { NumeronsService } from '../../../features/numerons/services/numerons.service';
import { panelSubtypeClasses } from '../../utils/panel-subtype-class-map';

@Component({
  selector: 'app-numeron-card',
  imports: [DatePipe, NgClass, RouterLink],
  templateUrl: './numeron-card.component.html',
})
export class NumeronCardComponent implements OnInit {
  private readonly numeronsService = inject(NumeronsService);
  private readonly logger = inject(Logger);
  private readonly router = inject(Router);
  public readonly numeron = input.required<NumeronItemModel>();
  public readonly numeronCollection = input.required<NumeronCollectionModel>();
  public readonly displayMode = input<'numeron' | 'collection-standard' | 'feed' | 'manage'>(
    'numeron',
  );
  public readonly isMyNumerons = input(false);
  public readonly isMyNumeronsByTopic = input(false);
  public readonly editReturnUrl = input('/mynumerons');
  public readonly deleteReturnUrl = input('/mynumerons');
  protected readonly numeronLikes = signal(0);
  protected readonly isCopying = signal(false);
  protected readonly copyErrorMessage = signal('');

  public ngOnInit(): void {
    this.numeronLikes.set(this.numeron().likes ?? 0);
  }

  protected byPublisherHref(userId: string): string {
    return `/numerons/bypublisher/${userId}`;
  }

  protected byTopicHref(topicId: string): string {
    return this.isMyNumerons() ? `/mynumerons/bytopic/${topicId}` : `/numerons/bytopic/${topicId}`;
  }

  protected byCategoryHref(category: string): string {
    const encodedCategory = encodeURIComponent(category);
    return this.isMyNumerons()
      ? `/mynumerons/bycategory/${encodedCategory}`
      : `/numerons/bycategory/${encodedCategory}`;
  }

  protected numeronHref(collectionId: string): string {
    return this.isMyNumerons()
      ? `/mynumerons/numeron/${collectionId}`
      : `/numerons/numeron/${collectionId}`;
  }

  protected editHref(collectionId: string): string {
    return `/mynumerons/numeron/${collectionId}/edit`;
  }

  protected deleteHref(collectionId: string): string {
    return `/mynumerons/${collectionId}/delete`;
  }

  protected editQueryParams(): { returnTo: string } {
    return { returnTo: this.editReturnUrl() };
  }

  protected manageQueryParams(): { returnTo: string } {
    return { returnTo: this.editReturnUrl() };
  }

  protected deleteQueryParams(): { returnTo: string } {
    return { returnTo: this.deleteReturnUrl() };
  }

  protected async copyNumeron(): Promise<void> {
    if (this.isCopying()) {
      return;
    }

    this.isCopying.set(true);
    this.copyErrorMessage.set('');

    this.numeronsService
      .copyMyNumeron(this.numeronCollection()._id)
      .pipe(take(1))
      .subscribe({
        next: async (collection) => {
          this.isCopying.set(false);
          await this.router.navigate(['/mynumerons/numeron', collection._id], {
            queryParams: {
              returnTo: this.deleteReturnUrl(),
            },
          });
        },
        error: (error) => {
          this.isCopying.set(false);
          this.copyErrorMessage.set('Oops, could not copy Numeron!');
          this.logger.warn('Failed to copy numeron.', {
            feature: 'numerons',
            operation: 'copy-numeron',
            severity: 'medium',
            error,
          });
        },
      });
  }

  protected hasExternalSource(url?: string): boolean {
    return typeof url === 'string' && url.trim().length > 0;
  }

  protected isCollectionStandardMode(): boolean {
    return this.displayMode() === 'collection-standard';
  }

  protected isFeedMode(): boolean {
    return this.displayMode() === 'feed';
  }

  protected isManageMode(): boolean {
    return this.displayMode() === 'manage';
  }

  protected panelSubtypeClasses(subType?: string): string {
    return panelSubtypeClasses(subType);
  }

  protected isPanelNumeron(numeron: NumeronItemModel): boolean {
    return Number(numeron.type) === 1;
  }

  protected likeNumeron(): void {
    this.numeronsService
      .likeNumeron(this.numeronCollection()._id, this.numeron()._id)
      .pipe(take(1))
      .subscribe({
        next: (response: unknown) => {
          const nModified = Number(
            (response as { nModified?: number | string; result?: { nModified?: number | string } })
              ?.nModified ??
              (response as { result?: { nModified?: number | string } })?.result?.nModified ??
              0,
          );

          if (nModified === 1) {
            this.numeronLikes.update((currentLikes) => currentLikes + 1);
          }
        },
        error: (error) => {
          this.logger.warn('Failed to like numeron.', {
            feature: 'numerons',
            operation: 'like-numeron',
            severity: 'medium',
            error,
          });
        },
      });
  }
}
